import { cn } from "../../../utils/cn.js";
import { CalendarPopover } from "../Calendar/Calendar.js";
import { cva } from "class-variance-authority";
import { CalendarDotsIcon, CaretDownIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { addDays, format, isValid, setDay } from "date-fns";
//#region src/components/DatePicker/DatePicker.tsx
const SEGMENT_CLASSES = cn("remove-global-styles inline-flex cursor-pointer items-center justify-center", "border border-(length:--border-width-default) border-solid border-border-primary", "bg-background-neutral-secondary text-text-primary", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", "disabled:cursor-not-allowed disabled:text-text-disabled", "hover:bg-background-neutral-secondary-hover");
const sideSegmentVariants = cva(SEGMENT_CLASSES, { variants: { size: {
	base: "h-[25px] w-[25px]",
	large: "h-[32px] w-[32px]"
} } });
const centerSegmentVariants = cva(SEGMENT_CLASSES, { variants: { size: {
	base: "h-[25px] px-xs text-body-base leading-body-base font-heading-medium",
	large: "h-[32px] px-sm text-body-medium leading-body-medium font-heading-medium"
} } });
const segmentIconVariants = cva("", { variants: { size: {
	base: "h-[12px] w-[12px]",
	large: "h-[12px] w-[12px]"
} } });
/**
* Checks whether a date value is valid and usable for comparisons.
*
* @param value - Candidate date value
* @returns True when value is a valid Date instance
*/
function isUsableDate(value) {
	return value instanceof Date && isValid(value);
}
/**
* Checks if a date falls within optional min/max boundaries.
*
* @param date - Date to validate
* @param minDate - Optional minimum date boundary
* @param maxDate - Optional maximum date boundary
* @returns True when the date is within the configured range
*/
function isWithinDateBounds(date, minDate, maxDate) {
	if (!isUsableDate(date)) return false;
	if (isUsableDate(minDate) && date < minDate) return false;
	if (isUsableDate(maxDate) && date > maxDate) return false;
	return true;
}
/**
* Curri-styled combined date picker built on CalendarPopover.
*
* Renders a segmented control that mirrors the Figma pattern: previous-day and
* next-day buttons around a central date trigger with calendar and caret icons.
* The center trigger opens a calendar popup for direct date selection while
* keeping the label synchronized with controlled or uncontrolled state.
*
* @param props - Date picker behavior, date bounds, and calendar configuration
* @returns The rendered segmented date picker
*/
function DatePicker({ calendarProps, className, dateFormat = "MM/dd/yyyy", defaultValue, disabled = false, maxDate, minDate, onValueChange, placeholder = "--/--/----", showTodayButton = false, size = "base", value, ...props }) {
	const [internalValue, setInternalValue] = React.useState(isUsableDate(defaultValue) ? defaultValue : void 0);
	const isControlled = value !== void 0;
	const selectedDate = isControlled ? isUsableDate(value) ? value : void 0 : internalValue;
	const setSelectedDate = React.useCallback((nextDate) => {
		if (!isWithinDateBounds(nextDate, minDate, maxDate)) return;
		if (!isControlled) setInternalValue(nextDate);
		onValueChange?.(nextDate);
	}, [
		isControlled,
		maxDate,
		minDate,
		onValueChange
	]);
	const handleCalendarChange = React.useCallback((nextDate) => {
		setSelectedDate(nextDate);
	}, [setSelectedDate]);
	const displayLabel = selectedDate ? format(selectedDate, dateFormat) : placeholder;
	const reservedLabels = React.useMemo(() => {
		if (!selectedDate || !/[Eec]/.test(dateFormat)) return null;
		const base = selectedDate;
		return Array.from({ length: 7 }, (_, day) => format(setDay(base, day), dateFormat));
	}, [selectedDate, dateFormat]);
	const prevDate = selectedDate ? addDays(selectedDate, -1) : void 0;
	const nextDate = selectedDate ? addDays(selectedDate, 1) : void 0;
	const disablePrev = disabled || !selectedDate || !prevDate || !isWithinDateBounds(prevDate, minDate, maxDate);
	const disableNext = disabled || !selectedDate || !nextDate || !isWithinDateBounds(nextDate, minDate, maxDate);
	return /* @__PURE__ */ jsxs("div", {
		className: cn("inline-flex items-center font-body", className),
		...props,
		children: [
			/* @__PURE__ */ jsx("button", {
				"aria-label": "Select previous day",
				className: cn(sideSegmentVariants({ size }), "rounded-l-xs"),
				disabled: disablePrev,
				onClick: () => {
					if (prevDate) setSelectedDate(prevDate);
				},
				type: "button",
				children: /* @__PURE__ */ jsx(CaretLeftIcon, { className: segmentIconVariants({ size }) })
			}),
			/* @__PURE__ */ jsx(CalendarPopover, {
				calendarProps,
				disabled,
				onValueChange: handleCalendarChange,
				showTodayButton,
				value: selectedDate,
				children: () => /* @__PURE__ */ jsxs("button", {
					className: cn(centerSegmentVariants({ size }), "gap-xxs border-l-0"),
					disabled,
					type: "button",
					children: [
						/* @__PURE__ */ jsx(CalendarDotsIcon, { className: segmentIconVariants({ size }) }),
						/* @__PURE__ */ jsxs("span", {
							className: "grid tabular-nums",
							children: [reservedLabels?.map((label, day) => /* @__PURE__ */ jsx("span", {
								"aria-hidden": true,
								className: "invisible col-start-1 row-start-1 whitespace-nowrap",
								children: label
							}, day)), /* @__PURE__ */ jsx("span", {
								className: cn("whitespace-nowrap", reservedLabels && "col-start-1 row-start-1"),
								children: displayLabel
							})]
						}),
						/* @__PURE__ */ jsx(CaretDownIcon, { className: segmentIconVariants({ size }) })
					]
				})
			}),
			/* @__PURE__ */ jsx("button", {
				"aria-label": "Select next day",
				className: cn(sideSegmentVariants({ size }), "border-l-0 rounded-r-xs"),
				disabled: disableNext,
				onClick: () => {
					if (nextDate) setSelectedDate(nextDate);
				},
				type: "button",
				children: /* @__PURE__ */ jsx(CaretRightIcon, { className: segmentIconVariants({ size }) })
			})
		]
	});
}
//#endregion
export { DatePicker };

//# sourceMappingURL=DatePicker.js.map